import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Lock,
  Truck,
  ShieldCheck,
  Loader2,
  ArrowRight,
  Tag,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

import { useCart } from "../context/CartContext";
import {
  fetchShippingRates,
  validateCoupon,
  createPaymentIntent,
  createSubscription,
} from "../lib/api";
import { stripePromise, stripeAppearance } from "../lib/stripe";

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

const emptyAddress = {
  name: "",
  email: "",
  phone: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "US",
};

const Field = ({ label, testid, required, ...props }) => (
  <div>
    <label className="font-display text-[10px] tracking-[0.3em] text-white/40 block mb-2">
      {label.toUpperCase()}{required && <span className="text-[#D10000]"> *</span>}
    </label>
    <input
      {...props}
      data-testid={testid}
      className="w-full bg-transparent border-b border-white/15 focus:border-[#D10000] outline-none py-2 text-white placeholder:text-white/30"
    />
  </div>
);

const Row = ({ label, value, emphasize, accent, testid }) => (
  <div className="flex items-center justify-between">
    <span className="text-white/55 uppercase tracking-widest text-xs">
      {label}
    </span>
    <span
      data-testid={testid}
      className={
        emphasize
          ? "font-display text-2xl tracking-wider text-white"
          : accent
          ? "text-[#D10000] text-sm font-semibold"
          : "text-white text-sm"
      }
    >
      {value}
    </span>
  </div>
);

// ---------- Inner payment form (only mounts once we have a client_secret) ----------
const PaymentForm = ({ total, onPaid, paymentIntentId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMsg("");

    const returnUrl = `${window.location.origin}/checkout/success?payment_intent=${paymentIntentId}`;

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      // We try to keep the customer on-site; only methods that require
      // a redirect (e.g. some bank-debit/wallets) will redirect.
      redirect: "if_required",
    });

    if (error) {
      setErrorMsg(error.message || "Payment failed. Try again.");
      setSubmitting(false);
      return;
    }

    if (paymentIntent && (paymentIntent.status === "succeeded" || paymentIntent.status === "processing")) {
      onPaid(paymentIntent);
      return;
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="payment-element-form" className="space-y-6">
      <PaymentElement options={{ layout: "tabs" }} />
      {errorMsg && (
        <div
          data-testid="payment-error"
          className="text-sm text-[#FF5252] border border-[#FF5252]/30 bg-[#FF5252]/5 px-4 py-3"
        >
          {errorMsg}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || submitting}
        data-testid="confirm-payment-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-[#D10000] hover:bg-[#A30000] disabled:opacity-40 disabled:cursor-not-allowed text-white font-display text-lg tracking-widest uppercase py-4 transition-colors"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" /> Processing…
          </>
        ) : (
          <>
            <Lock className="h-4 w-4" /> Pay ${total.toFixed(2)}
          </>
        )}
      </button>
    </form>
  );
};

export default function Checkout() {
  const { items, subtotal, count, clearCart } = useCart();
  const navigate = useNavigate();

  const [address, setAddress] = useState(emptyAddress);
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);

  // Coupon state
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount_amount, coupon }
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  // PaymentIntent state
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [creatingIntent, setCreatingIntent] = useState(false);
  const [serverTotal, setServerTotal] = useState(null); // authoritative numbers from backend

  const change = (k) => (e) =>
    setAddress((a) => ({ ...a, [k]: e.target.value }));

  const discount = appliedCoupon?.discount_amount || 0;
  const shippingCost = selectedRate?.amount || 0;
  const localTotal = useMemo(
    () => Math.max(0, subtotal - discount) + shippingCost,
    [subtotal, discount, shippingCost]
  );
  // Prefer server-calculated total once we've fetched the PaymentIntent
  const total = serverTotal?.amount ?? localTotal;

  // ----- Reset PaymentIntent when key inputs change (cart / rate / coupon) -----
  useEffect(() => {
    setClientSecret(null);
    setPaymentIntentId(null);
    setServerTotal(null);
  }, [JSON.stringify(items), selectedRate?.object_id, appliedCoupon?.code]);

  // ----- Shipping rate fetch -----
  const fetchRates = async (e) => {
    e?.preventDefault?.();
    if (!items.length) {
      toast.error("Your cart is empty.");
      return;
    }
    setLoadingRates(true);
    setRates([]);
    setSelectedRate(null);
    try {
      const res = await fetchShippingRates({
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
        address,
      });
      setRates(res.rates || []);
      if (res.rates?.length) {
        setSelectedRate(res.rates[0]);
        toast.success(`${res.rates.length} shipping options found.`);
      } else {
        toast.error("No rates returned for this address.");
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : detail?.message || "Could not fetch shipping rates."
      );
    } finally {
      setLoadingRates(false);
    }
  };

  // ----- Coupon -----
  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code) {
      toast.error("Enter a coupon code first.");
      return;
    }
    setValidatingCoupon(true);
    try {
      const res = await validateCoupon({
        code,
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
        shipping_rate: selectedRate
          ? {
              object_id: selectedRate.object_id,
              amount: selectedRate.amount,
              provider: selectedRate.provider,
              servicelevel_name: selectedRate.servicelevel_name,
            }
          : null,
      });
      setAppliedCoupon({
        code: res.code,
        discount_amount: res.discount_amount,
        coupon: res.coupon,
      });
      toast.success(`Coupon ${res.code} applied — $${res.discount_amount.toFixed(2)} off.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Coupon invalid.");
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput("");
  };

  // True when ANY item in cart is a Subscribe & Save variant
  const hasSubscription = useMemo(
    () => items.some((i) => /-sub$/.test(i.product_id)),
    [items]
  );
  const allSubscription = useMemo(
    () => items.length > 0 && items.every((i) => /-sub$/.test(i.product_id)),
    [items]
  );
  const mixedCart = hasSubscription && !allSubscription;

  // ----- Create PaymentIntent (proceeds to payment step) -----
  const startPayment = async () => {
    if (!selectedRate) {
      toast.error("Choose a shipping option first.");
      return;
    }
    if (!address.email) {
      toast.error("Add your email above so we can send your receipt.");
      return;
    }
    if (mixedCart) {
      toast.error(
        "You can't mix Subscribe & Save with one-time items in the same order. Place them as separate orders."
      );
      return;
    }
    if (hasSubscription && appliedCoupon) {
      toast.error(
        "Coupons aren't supported on Subscribe & Save yet. Remove the coupon to continue."
      );
      return;
    }
    setCreatingIntent(true);
    try {
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
        })),
        customer_email: address.email || null,
        shipping_address: address,
        shipping_rate: {
          object_id: selectedRate.object_id,
          amount: selectedRate.amount,
          provider: selectedRate.provider,
          servicelevel_name: selectedRate.servicelevel_name,
        },
      };
      const res = hasSubscription
        ? await createSubscription(payload)
        : await createPaymentIntent({
            ...payload,
            promotion_code: appliedCoupon?.code || null,
          });
      setClientSecret(res.client_secret);
      setPaymentIntentId(res.payment_intent_id);
      setServerTotal({
        amount: res.amount,
        subtotal: res.subtotal,
        shipping_amount: res.shipping_amount,
        discount_amount: res.discount_amount,
      });
      // Smooth scroll to payment section
      setTimeout(() => {
        document.getElementById("payment-section")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 100);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Could not start checkout."
      );
    } finally {
      setCreatingIntent(false);
    }
  };

  const handlePaid = (paymentIntent) => {
    clearCart();
    navigate(`/checkout/success?payment_intent=${paymentIntent.id}`);
  };

  if (!items.length) {
    return (
      <div className="bg-[#050505] text-white min-h-[70vh] flex items-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
          <h1
            className="font-display uppercase leading-[0.9]"
            style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)" }}
          >
            Your cart is empty.
          </h1>
          <Link
            to="/shop"
            data-testid="checkout-empty-shop-link"
            className="mt-8 inline-flex items-center gap-3 bg-[#D10000] hover:bg-[#A30000] text-white font-display tracking-widest uppercase px-8 py-4"
          >
            Shop the Line <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050505] text-white" data-testid="checkout-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-20 border-b border-white/10">
        <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
          SECURE CHECKOUT
        </div>
        <h1
          className="font-display uppercase leading-[0.9] tracking-tight"
          style={{ fontSize: "clamp(2.5rem, 7vw, 6rem)" }}
        >
          Checkout
        </h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
        {/* LEFT: Form */}
        <div className="lg:col-span-7 space-y-14">
          {/* 01 — Address */}
          <form onSubmit={fetchRates} data-testid="shipping-address-form">
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
              01 · SHIP TO
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <Field label="Full Name" required testid="checkout-name-input" value={address.name} onChange={change("name")} />
              <Field label="Email" type="email" required testid="checkout-email-input" value={address.email} onChange={change("email")} />
              <div className="md:col-span-2">
                <Field label="Street Address" required testid="checkout-street1-input" value={address.street1} onChange={change("street1")} />
              </div>
              <div className="md:col-span-2">
                <Field label="Apt / Suite (optional)" testid="checkout-street2-input" value={address.street2} onChange={change("street2")} />
              </div>
              <Field label="City" required testid="checkout-city-input" value={address.city} onChange={change("city")} />
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="font-display text-[10px] tracking-[0.3em] text-white/40 block mb-2">STATE *</label>
                  <select
                    required
                    data-testid="checkout-state-input"
                    value={address.state}
                    onChange={change("state")}
                    className="w-full bg-transparent border-b border-white/15 focus:border-[#D10000] outline-none py-2 text-white"
                  >
                    <option value="" className="bg-[#0A0A0A]">—</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s} className="bg-[#0A0A0A]">{s}</option>
                    ))}
                  </select>
                </div>
                <Field label="ZIP" required testid="checkout-zip-input" value={address.zip} onChange={change("zip")} />
              </div>
              <Field label="Phone" type="tel" required testid="checkout-phone-input" value={address.phone} onChange={change("phone")} />
            </div>
            <button
              type="submit"
              disabled={loadingRates}
              data-testid="get-rates-button"
              className="mt-10 inline-flex items-center gap-3 border border-white/20 hover:border-[#D10000] disabled:opacity-50 text-white font-display tracking-widest uppercase px-6 py-3 transition-colors"
            >
              {loadingRates ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Calculating…</>
              ) : (
                <><Truck className="h-4 w-4" /> Get Shipping Rates</>
              )}
            </button>
          </form>

          {/* 02 — Rates */}
          {rates.length > 0 && (
            <div data-testid="shipping-rates">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                02 · CHOOSE SPEED
              </div>
              <ul className="border border-white/10 divide-y divide-white/5">
                {rates.map((r) => {
                  const active = selectedRate?.object_id === r.object_id;
                  return (
                    <li key={r.object_id}>
                      <button
                        type="button"
                        onClick={() => setSelectedRate(r)}
                        data-testid={`rate-${r.object_id}`}
                        className={`w-full text-left flex items-center justify-between gap-6 px-6 py-5 transition-colors ${
                          active
                            ? "bg-[#D10000]/10 border-l-2 border-l-[#D10000]"
                            : "hover:bg-white/5 border-l-2 border-l-transparent"
                        }`}
                      >
                        <div>
                          <div className="font-display text-lg tracking-tight uppercase">
                            {r.provider} · {r.servicelevel_name}
                          </div>
                          <div className="text-white/50 text-xs mt-1">
                            {r.estimated_days
                              ? `Arrives in ~${r.estimated_days} business day${r.estimated_days > 1 ? "s" : ""}`
                              : "Standard delivery"}
                          </div>
                        </div>
                        <div className="font-display text-2xl tracking-wider">${r.amount.toFixed(2)}</div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* 03 — Coupon */}
          {selectedRate && !hasSubscription && (
            <div data-testid="coupon-section">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                03 · COUPON CODE <span className="text-white/40">(optional)</span>
              </div>
              {appliedCoupon ? (
                <div
                  data-testid="coupon-applied"
                  className="border border-emerald-500/30 bg-emerald-500/5 px-5 py-4 flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" />
                    <div className="min-w-0">
                      <div className="font-display tracking-widest uppercase text-sm truncate">
                        {appliedCoupon.code}
                      </div>
                      <div className="text-emerald-300/80 text-xs">
                        − ${appliedCoupon.discount_amount.toFixed(2)} off subtotal
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    data-testid="remove-coupon-button"
                    className="text-white/50 hover:text-[#FF5252] inline-flex items-center gap-1 text-xs uppercase tracking-widest"
                  >
                    <XCircle className="h-4 w-4" /> Remove
                  </button>
                </div>
              ) : (
                <div className="flex items-stretch gap-3">
                  <div className="flex-1 relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/30" />
                    <input
                      type="text"
                      data-testid="coupon-input"
                      placeholder="Enter promo code"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      className="w-full bg-transparent border border-white/15 focus:border-[#D10000] outline-none pl-10 pr-4 py-3 text-white placeholder:text-white/30 uppercase tracking-widest text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={validatingCoupon || !couponInput.trim()}
                    data-testid="apply-coupon-button"
                    className="border border-white/20 hover:border-[#D10000] disabled:opacity-40 text-white font-display tracking-widest uppercase px-5 transition-colors"
                  >
                    {validatingCoupon ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* 04 — Payment Element */}
          {selectedRate && (
            <div id="payment-section" data-testid="payment-section">
              <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
                04 · PAYMENT
              </div>
              {!clientSecret ? (
                <button
                  type="button"
                  onClick={startPayment}
                  disabled={creatingIntent}
                  data-testid="proceed-to-payment-button"
                  className="w-full md:w-auto inline-flex items-center justify-center gap-3 bg-[#D10000] hover:bg-[#A30000] disabled:opacity-40 text-white font-display tracking-widest uppercase px-10 py-4 transition-colors"
                >
                  {creatingIntent ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Preparing…</>
                  ) : (
                    <><Lock className="h-4 w-4" /> Continue to Payment</>
                  )}
                </button>
              ) : (
                <div className="border border-white/10 bg-[#0A0A0A] p-6 md:p-8">
                  <Elements
                    stripe={stripePromise}
                    options={{ clientSecret, appearance: stripeAppearance }}
                  >
                    <PaymentForm
                      total={total}
                      paymentIntentId={paymentIntentId}
                      onPaid={handlePaid}
                    />
                  </Elements>
                  <div className="mt-6 flex items-center justify-center gap-4 text-xs text-white/45">
                    <span className="inline-flex items-center gap-2">
                      <ShieldCheck className="h-3.5 w-3.5 text-[#D10000]" /> Secured by Stripe
                    </span>
                    <span className="inline-flex items-center gap-2">
                      <Lock className="h-3.5 w-3.5 text-[#D10000]" /> 256-bit SSL
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Summary */}
        <aside className="lg:col-span-5">
          <div className="border border-white/10 bg-[#0A0A0A] p-8 md:p-10 lg:sticky lg:top-28">
            <div className="font-display text-xs tracking-[0.3em] text-[#D10000] mb-4">
              ORDER · {count} ITEM{count !== 1 ? "S" : ""}
              {hasSubscription && (
                <span className="ml-2 px-2 py-0.5 bg-[#D10000]/20 text-[#D10000] tracking-widest" data-testid="subscription-badge">
                  AUTO-RENEW MONTHLY
                </span>
              )}
            </div>
            <ul className="space-y-4 mb-8">
              {items.map((i) => (
                <li
                  key={i.product_id}
                  className="flex items-center gap-4"
                  data-testid={`summary-item-${i.product_id}`}
                >
                  <div className="w-14 h-14 bg-[#111] border border-white/10 shrink-0 overflow-hidden">
                    <img src={i.image} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-sm tracking-wider uppercase truncate">{i.name}</div>
                    <div className="text-white/40 text-xs">
                      Qty {i.quantity} · ${i.price.toFixed(2)}
                    </div>
                  </div>
                  <div className="font-display tracking-wider text-sm">
                    ${(i.price * i.quantity).toFixed(2)}
                  </div>
                </li>
              ))}
            </ul>

            <div className="border-t border-white/10 pt-6 space-y-3 text-sm">
              <Row label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
              {appliedCoupon && (
                <Row
                  label={`Coupon · ${appliedCoupon.code}`}
                  value={`− $${discount.toFixed(2)}`}
                  accent
                  testid="summary-discount"
                />
              )}
              <Row
                label="Shipping"
                value={selectedRate ? `$${shippingCost.toFixed(2)}` : "—"}
              />
              <div className="h-px bg-white/10 my-2" />
              <Row
                label="Total"
                value={`$${total.toFixed(2)}`}
                emphasize
                testid="checkout-total"
              />
            </div>

            <div className="mt-8 grid grid-cols-2 gap-3 text-xs text-white/45">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-3.5 w-3.5 text-[#D10000]" />
                Secured by Stripe
              </div>
              <div className="flex items-center gap-2">
                <Truck className="h-3.5 w-3.5 text-[#D10000]" />
                Rates by Shippo
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
